import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Chip,
    Box,
    Typography,
    FormHelperText,
    Button,
    IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from './Tooltip';
import useDropdownStyles from './styles/Dropdown';

const MultiSelectDropdown = ({
    id = "custom-multiselect-dropdown",
    items = [],
    value = [],
    onChange,
    label,
    placeholder = "Select options",
    required = false,
    disabled = false,
    size = "medium",
    width = "100%",
    filled = true,
    color = "greyDark",
    background = "secondary",
    showPlaceholder = true,
    error = false,
    helperText,
    showSelectAll = false,
    showSelectEligible = false,
    onSelectEligible,
    excludedCodes = [],
    compactSelection = false,
    renderSelectionSummary,
    ...props
}) => {
    const classes = useDropdownStyles();

    const handleChange = (event) => {
        const selectedValues = event.target.value;

        const syntheticEvent = {
            target: {
                name: id,
                value: selectedValues,
            },
        };

        onChange(syntheticEvent);
    };

    const handleSelectAll = () => {
        const allValues = items.map(item => item.value);
        const syntheticEvent = {
            target: {
                name: id,
                value: allValues,
            },
        };
        onChange(syntheticEvent);
    };

    const handleDeselectAll = () => {
        const syntheticEvent = {
            target: {
                name: id,
                value: [],
            },
        };
        onChange(syntheticEvent);
    };

    const getSelectedItems = () => {
        return items.filter(item => value.includes(item.value));
    };

    const selectedItems = getSelectedItems();
    const selectedCount = selectedItems.length;

    return (
        <Box style={{ width: "100%", maxWidth: "600px" }}>
            <FormControl required={required} variant="outlined" fullWidth style={{ width }}>
                <InputLabel
                    id={`${id}-label`}
                    sx={{
                        marginTop: value && value.length > 0 ? "0.7rem" : "0rem",
                        marginLeft: "-0.9rem",
                        "&.Mui-focused": {
                            marginTop: "0.7rem"
                        },
                    }}
                >
                    {label || placeholder}
                </InputLabel>
                <Select
                    id={id}
                    labelId={`${id}-label`}
                    multiple
                    disabled={disabled}
                    value={value}
                    displayEmpty={showPlaceholder}
                    color={background}
                    size={size}
                    style={{ color, height: "100%" }}
                    autoWidth={!width}
                    variant="standard"
                    classes={{
                        filled: classes[`${background}_${(filled ? "filled" : "outlined")}`],
                        iconFilled: classes[`${background}_${(filled ? "filled" : "outlined")}`],
                    }}
                    renderValue={(selected) => (
                        <Box className={classes.chips}>
                            {selected.length === 0 ? (
                                ""
                            ) : compactSelection ? (
                                <Typography variant="body2">{selected.length} επιλεγμένες προβατίνες</Typography>
                            ) : (
                                selected.map((val) => {
                                    const item = items.find(item => item.value === val);
                                    return (
                                        <Chip
                                            key={val}
                                            label={item?.label || item?.text || val}
                                            className={classes.chip}
                                            size="small"
                                            variant="outlined"
                                        />
                                    );
                                })
                            )}
                        </Box>
                    )}
                    onChange={handleChange}
                    sx={{
                        display: "flex",
                        textAlign: "left"
                    }}
                    {...props}
                >
                    {showSelectAll && items.length > 0 && (
                        <Box sx={{ p: 1, backgroundColor: 'rgba(0, 0, 0, 0.04)', position: 'sticky', top: 0, zIndex: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelectAll();
                                    }}
                                    variant="outlined"
                                    sx={{ flex: 1, minWidth: 130, fontSize: '0.75rem' }}
                                >
                                    Επιλογή Όλων
                                </Button>
                                {showSelectEligible && (
                                    <Box sx={{ flex: 1, minWidth: 150, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                        <Button
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSelectEligible?.();
                                            }}
                                            variant="contained"
                                            sx={{ flex: 1, fontSize: '0.75rem' }}
                                        >
                                            Επιλογή Επιλέξιμων
                                        </Button>
                                        <Tooltip title="Επιλέγει τα ζώα που πληρούν τους ελέγχους για την συγκεκριμένη εκτροφή και τις επιλεγμένες ημερομηνίες. Κατά την υποβολή εκτελούνται ξανά οι έλεγχοι από τον διακομιστή.">
                                            <IconButton
                                                size="small"
                                                aria-label="Πληροφορίες για την επιλογή επιλέξιμων ζώων"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                                <Button
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeselectAll();
                                    }}
                                    variant="outlined"
                                    sx={{ flex: 1, minWidth: 130, fontSize: '0.75rem' }}
                                >
                                    Αποεπιλογή Όλων
                                </Button>
                            </Box>
                        </Box>
                    )}
                    {items.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                            <Checkbox
                                checked={value.indexOf(item.value) > -1}
                                color="primary"
                            />
                            <ListItemText primary={item.label || item.text} />
                        </MenuItem>
                    ))}
                </Select>

                {/* Selected items note */}
                {!renderSelectionSummary && selectedCount > 0 && (
                    <Typography className={classes.selectedNote}>
                        Επιλεγμένες Προβατίνες ({selectedCount}): {selectedItems.map(item => item.label || item.text).join(', ')}
                    </Typography>
                )}

                {excludedCodes.length > 0 && (
                    <Box sx={{ mt: 1, maxHeight: 160, overflowY: 'auto', textAlign: 'left' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                            Δεν επιλέχθηκαν ({excludedCodes.length}): {excludedCodes.join(', ')}.
                            {' '}Δεν πληρούν τους ελέγχους για τις επιλεγμένες ημερομηνίες, π.χ. λόγω άλλης σύζευξης ή τοκετού.
                        </Typography>
                    </Box>
                )}

                {helperText && (
                    <FormHelperText error={error}>
                        {helperText}
                    </FormHelperText>
                )}
            </FormControl>
            {renderSelectionSummary?.(value)}
        </Box>
    );
};

export default MultiSelectDropdown;
